/**
 * Drag handles for a preview frame: right / bottom / corner, the same gesture
 * as the snapshot editor's preview (containerSnapshotPreview.js, which keeps
 * its own copy).
 *
 * Sizes are device pixels; the frame on screen is that size times `scale`, so
 * a pointer move converts back through it. The frame is centred, so an edge
 * moves half the size change and the size grows by twice the pointer travel.
 *
 * The pointer is captured on the handle for the whole drag: the frame is an
 * iframe, and a pointer that crosses into one stops reaching this document, so
 * without capture a quick inward drag loses its pointerup and the frame keeps
 * following the mouse.
 *
 * @returns {{ size: {w, h} | null, setSize, isDragging: boolean, startDrag }}
 *   `size` is the dragged size, null while the device preset applies.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const MIN_SIZE = 120;
const CURSOR = { right: "ew-resize", bottom: "ns-resize", corner: "nwse-resize" };

/**
 * The size a drag has reached: `start` { w, h } in device pixels, the pointer
 * travel in screen pixels, the frame's scale and the largest size allowed.
 * An edge only changes its own axis; the frame is centred, so it grows by
 * twice the travel.
 */
export function draggedSize(start, edge, travel, scale, max) {
  const dx = travel.x / scale;
  const dy = travel.y / scale;
  const clamp = (value, limit) => Math.min(Math.max(MIN_SIZE, limit), Math.max(MIN_SIZE, Math.round(value)));
  return {
    w: edge === "bottom" ? start.w : clamp(start.w + 2 * dx, max.w),
    h: edge === "right" ? start.h : clamp(start.h + 2 * dy, max.h),
  };
}

export default function useFrameResize() {
  const [size, setSize] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const cleanupRef = useRef(null);

  // Unmounting mid-drag must still give the page its cursor and text selection back.
  useEffect(() => () => cleanupRef.current && cleanupRef.current(), []);

  /**
   * @param event   the handle's pointerdown
   * @param edge    "right" | "bottom" | "corner"
   * @param from    { width, height, scale, maxWidth, maxHeight, onEnd? } — the
   *                size on screen now, its scale, the largest size the stage
   *                holds, and a callback with the final { w, h }
   */
  const startDrag = useCallback((event, edge, from) => {
    event.preventDefault();
    event.stopPropagation();
    if (cleanupRef.current) cleanupRef.current();

    const target = event.currentTarget;
    const { pointerId } = event;
    const start = { x: event.clientX, y: event.clientY, w: from.width, h: from.height };
    let last = { w: from.width, h: from.height };
    try {
      target.setPointerCapture(pointerId);
    } catch (_err) {
      // Without capture the drag still works; it can just be cut short by the iframe.
    }
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = CURSOR[edge];
    document.body.style.userSelect = "none";
    setIsDragging(true);
    setSize(last);

    const onMove = (moveEvent) => {
      last = draggedSize(
        { w: start.w, h: start.h },
        edge,
        { x: moveEvent.clientX - start.x, y: moveEvent.clientY - start.y },
        from.scale,
        { w: from.maxWidth, h: from.maxHeight }
      );
      setSize(last);
    };

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", finish);
      document.removeEventListener("pointercancel", finish);
      window.removeEventListener("blur", finish);
      target.removeEventListener("lostpointercapture", finish);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = null;
      try {
        if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
      } catch (_err) {
        // Already released by the browser.
      }
      setIsDragging(false);
      if (from.onEnd) from.onEnd(last);
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", finish);
    document.addEventListener("pointercancel", finish);
    window.addEventListener("blur", finish);
    target.addEventListener("lostpointercapture", finish);
    cleanupRef.current = finish;
  }, []);

  return { size, setSize, isDragging, startDrag };
}
